'use client';

import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Box, useBreakpointValue, Button, Spinner, AbsoluteCenter, Heading, Text, Flex } from '@chakra-ui/react';
import { SHARE_VIDEOS } from 'services/shared/imageService';
import { useSession } from 'components/useSession';
import { ROUTES } from 'services/shared/routerService';
import { useRouter } from 'next/router';

const BATCH_SIZE = 12;

const MosaicGrid: React.FC = () => {
	const columns = useBreakpointValue({ base: 2, xs: 2, sm: 2, md: 3, lg: 4, xl: 4 });

	const [shuffledVideos, setShuffledVideos] = useState<any[]>([]);
	const [displayedCount, setDisplayedCount] = useState(0);
	const [hasLoadedAll, setHasLoadedAll] = useState(false);

	const [loadingStates, setLoadingStates] = useState<Record<string, boolean>>({});

	const videoRefs = useRef<Map<string, HTMLVideoElement>>(new Map());
	const router = useRouter();
	// @ts-ignore
	const { data: session } = useSession();

	useEffect(() => {
		const videoArray = Object.entries(SHARE_VIDEOS)
			.filter(([key]) => key.startsWith('vmp4_'))
			.map(([key, url]) => ({
				key,
				src: url as string,
				poster: url as string,
			}));

		const shuffled = [...videoArray].sort(() => Math.random() - 0.5);

		setShuffledVideos(shuffled);
		setDisplayedCount(Math.min(BATCH_SIZE, shuffled.length));
	}, []);

	useEffect(() => {
		setLoadingStates((prev) => {
			const newStates = { ...prev };
			shuffledVideos.slice(0, displayedCount).forEach((video) => {
				if (!(video.key in newStates)) {
					newStates[video.key] = true;
				}
			});
			return newStates;
		});
	}, [displayedCount, shuffledVideos]);

	const observer = useRef<IntersectionObserver | null>(null);

	const handleIntersection = useCallback((entries: IntersectionObserverEntry[]) => {
		entries.forEach((entry) => {
			const video = videoRefs.current.get(entry.target.id);
			if (!video) return;

			if (entry.isIntersecting) {
				video.play().catch(() => {});
			} else {
				video.pause();
			}
		});
	}, []);

	useEffect(() => {
		observer.current = new IntersectionObserver(handleIntersection, {
			threshold: 0.6,
			rootMargin: '-50px 0px -50px 0px',
		});

		return () => observer.current?.disconnect();
	}, [handleIntersection]);

	useEffect(() => {
		const currentObserver = observer.current;
		if (!currentObserver) return;

		videoRefs.current.forEach((video) => {
			if (video) currentObserver.observe(video);
		});

		return () => {
			videoRefs.current.forEach((video) => {
				if (video) currentObserver.unobserve(video);
			});
		};
	}, [displayedCount]);

	const handleVideoLoaded = useCallback((key: string) => {
		setLoadingStates((prev) => ({ ...prev, [key]: false }));
	}, []);

	const handleViewMore = () => {
		setDisplayedCount((prev) => {
			const next = prev + BATCH_SIZE;
			if (next >= shuffledVideos.length) {
				setHasLoadedAll(true);
				return shuffledVideos.length;
			}
			return next;
		});
	};

	const handleVideoClick = (e: React.MouseEvent) => {
		e.preventDefault();
		if (session?.user) {
			router.push(ROUTES.STUDIO);
		} else {
			router.push(ROUTES.REGISTER);
		}
	};

	const displayedVideos = shuffledVideos.slice(0, displayedCount);

	return (
		<Box maxW="1400px" mx="auto" px={{ base: 0, sm: 0, md: 6, lg: 8 }} py={10}>
			{/*<Text fontSize="3xl" fontWeight="bold" mb={8} ml={4} textAlign="left">*/}
			{/*	Discover*/}
			{/*</Text>*/}

			<Flex
				direction={{ base: 'column', md: 'row' }}
				align={{ base: 'start', md: 'end' }}
				justify="space-between"
				gap={10}
				mb={16}
			>
				<Box maxW="2xl">
					{/*<Text*/}
					{/*	fontSize="11px"*/}
					{/*	fontWeight="500"*/}
					{/*	textTransform="uppercase"*/}
					{/*	letterSpacing="0.22em"*/}
					{/*	color="gray.400"*/}
					{/*>*/}
					{/*	Real campaigns • Real brands*/}
					{/*</Text>*/}
					<Heading
						mt={5}
						fontSize={{ base: '2.5rem', md: '4.5rem' }}
						fontWeight={400}
						lineHeight={1}
						letterSpacing="-0.035em"
					>
						Discover {/*<Text as="span" fontWeight={400} color="gray.500">*/}
						{/*	shipped.*/}
						{/*</Text>*/}
					</Heading>
				</Box>
				<Text maxW="sm" fontSize="15px" lineHeight="relaxed" color="gray.500">
					Every video below was generated in imagemotion
				</Text>
			</Flex>

			<Box
				columnCount={columns}
				columnGap={{ base: 1, md: 4 }}
				w="full"
				css={{
					'& > *': {
						breakInside: 'avoid',
						minWidth: 'min(100%, 280px)',
						width: '100%',
					},
				}}
			>
				{displayedVideos.map((video) => {
					const isLoading = loadingStates[video.key] ?? true;

					return (
						<Box
							key={video.key}
							onClick={handleVideoClick}
							mb={{ base: 1, md: 4 }}
							breakInside="avoid"
							borderRadius="2xl"
							overflow="hidden"
							boxShadow="lg"
							bg="gray.900"
							transition="all 0.3s ease"
							position="relative"
							_hover={{
								cursor: 'pointer',
								transform: 'translateY(-6px)',
								boxShadow: '2xl',
							}}
						>
							<Box
								as="video"
								id={video.key}
								ref={(el) => {
									if (el) videoRefs.current.set(video.key, el);
									else videoRefs.current.delete(video.key);
								}}
								// @ts-ignore
								src={video.src}
								poster={video.poster}
								muted
								loop
								playsInline
								controls={false}
								preload="auto"
								w="100%"
								onLoadedData={() => handleVideoLoaded(video.key)}
								onCanPlay={() => handleVideoLoaded(video.key)}
								onError={() => handleVideoLoaded(video.key)}
								sx={{
									display: 'block',
									height: 'auto',
									objectFit: 'cover',
									aspectRatio: 'auto',
								}}
							/>

							{isLoading && (
								<AbsoluteCenter axis="both">
									<Spinner color="blue.500" size="xl" />
								</AbsoluteCenter>
							)}
						</Box>
					);
				})}
			</Box>

			{!hasLoadedAll && displayedCount < shuffledVideos.length && (
				<Box textAlign="center" mt={12} mb={8}>
					<Button
						onClick={handleViewMore}
						size="lg"
						colorScheme="blue"
						px={12}
						py={6}
						fontSize="md"
						fontWeight="semibold"
						boxShadow="md"
						_hover={{
							transform: 'scale(1.05)',
							boxShadow: 'lg',
						}}
					>
						View More
					</Button>
				</Box>
			)}
		</Box>
	);
};

export default MosaicGrid;
